import React from "react";
import { Box, Typography, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PillCheckbox from "../../../Components/PillCheckbox";

const CUSTOM_MAX = 150;

// Border states match Figma: grey (#D0D5DD) when blurred/default, purple
// (#6D72F6) when focused, 8px radius throughout.
const customInputSx = {
    "& .MuiOutlinedInput-root": {
        borderRadius: "8px",
        fontSize: "14px",
        "& fieldset": { borderColor: "#D0D5DD" },
        "&:hover fieldset": { borderColor: "#D0D5DD" },
        "&.Mui-focused fieldset": { borderColor: "#6D72F6", borderWidth: "1.5px" },
    },
};

// "Problem reported" — predefined checkboxes from orgMeta plus a single optional
// custom free-text reason (150 chars). Only one custom reason is allowed: the
// "+ Add custom problem reported" link is shown only while none exists, and
// removing the custom reason brings the link back.
//
// Observation is deliberately NOT here: it is answered per pack, against that pack's
// own warranty status, so it lives on PackReplacementCard.
export default function ProblemReportedSection({
    options = [],
    allowCustom = true,
    selectedCodes = [],
    customProblems = [],
    onToggleCode = () => {},
    onAddCustom = () => {},
    onChangeCustom = () => {},
    onRemoveCustom = () => {},
    hasError = false,
}) {
    const hasCustom = customProblems.length > 0;

    return (
        <Box
            sx={{
                border: "1px solid #EAEAEA",
                borderRadius: "12px",
                padding: "24px 28px",
                backgroundColor: "#fff",
            }}
        >
            <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#1A1A1A", mb: "12px" }}>
                Problem reported<span style={{ color: "#E5484D" }}>*</span>
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px" }}>
                {options.map((opt) => (
                    <PillCheckbox
                        key={opt.code}
                        testId={`problem-${opt.code}`}
                        checked={selectedCodes.includes(opt.code)}
                        onChange={() => onToggleCode(opt.code)}
                        label={opt.label}
                    />
                ))}
            </Box>

            {hasError && (
                <Typography sx={{ fontSize: "12px", color: "#E5484D", mt: "8px" }}>
                    Please select at least one problem reported
                </Typography>
            )}

            {hasCustom && (
                <Box sx={{ mt: "16px", maxWidth: "560px" }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1A1A1A", mb: "6px" }}>
                        Add custom problem reported
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        autoFocus
                        placeholder="Type your custom problem reported"
                        value={customProblems[0]}
                        sx={customInputSx}
                        inputProps={{ maxLength: CUSTOM_MAX, "data-testid": "custom-problem-0" }}
                        onChange={(e) => onChangeCustom(0, e.target.value)}
                    />
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: "6px" }}>
                        <Typography
                            onClick={() => onRemoveCustom(0)}
                            data-testid="custom-problem-remove-0"
                            sx={{ fontSize: "13px", fontWeight: 500, color: "#E5484D", cursor: "pointer" }}
                        >
                            Remove
                        </Typography>
                        <Typography sx={{ fontSize: "13px", color: "#A0A0A0" }}>
                            {customProblems[0].length} /{CUSTOM_MAX}
                        </Typography>
                    </Box>
                </Box>
            )}

            {allowCustom && !hasCustom && (
                <Box
                    onClick={onAddCustom}
                    data-testid="add-custom-problem"
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        mt: "16px",
                        cursor: "pointer",
                        color: "#1A1A1A",
                    }}
                >
                    <AddIcon sx={{ fontSize: 20 }} />
                    <Typography sx={{ fontSize: "15px" }}>Add custom problem reported</Typography>
                </Box>
            )}

        </Box>
    );
}
